import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, Clock, Briefcase, ChevronLeft, CheckCircle2, Upload, 
  FileText, X, Loader2, Code2, Building2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JobDetail = () => {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    resume: null
  });

  useEffect(() => {
    fetchJob();
  }, [slug]);

  const fetchJob = async () => {
    try {
      const response = await fetch(`${API_URL}/api/careers/jobs/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setJob(data);
      } else {
        setJob(null);
      }
    } catch (error) {
      console.error('Error fetching job:', error);
      setJob(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload a PDF or Word document');
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      setFormData(prev => ({ ...prev, resume: file }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.resume) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const formPayload = new FormData();
      formPayload.append('job_id', job.id);
      formPayload.append('name', formData.name);
      formPayload.append('email', formData.email);
      formPayload.append('phone', formData.phone);
      formPayload.append('resume', formData.resume);
      
      const response = await fetch(`${API_URL}/api/careers/apply`, {
        method: 'POST',
        body: formPayload
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        setSubmitted(true);
        toast.success('Application submitted successfully!');
      } else {
        toast.error(result.detail || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#320547] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Job Not Found</h1>
          <p className="text-gray-600 mb-6">The position you're looking for doesn't exist or has been closed.</p>
          <Link to="/careers">
            <Button>View All Jobs</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="job-detail-page">
      <SEO 
        title={`${job.title} - Careers | Navaris Digital`}
        description={job.role_summary || `Apply for ${job.title} position at Navaris Digital`}
        canonicalUrl={`/careers/${slug}`}
      />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-12 bg-gradient-to-br from-[#320547] via-[#4a1a6b] to-[#95228A]">
        <div className="container mx-auto px-6">
          <Link 
            to="/careers" 
            className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors"
            data-testid="back-to-careers"
          >
            <ChevronLeft size={20} />
            Back to all jobs
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-white/20 text-white text-sm rounded-full">
                {job.employment_type}
              </span>
              {job.is_active && (
                <span className="px-3 py-1 bg-green-500/20 text-green-300 text-sm rounded-full">
                  Actively Hiring
                </span>
              )}
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4" data-testid="job-title">
              {job.title}
            </h1>
            
            <p className="text-xl text-[#f0c674] font-medium mb-6">{job.designation}</p>
            
            <div className="flex flex-wrap items-center gap-6 text-white/80">
              <span className="flex items-center gap-2">
                <MapPin size={18} />
                {job.location}
              </span>
              <span className="flex items-center gap-2">
                <Clock size={18} />
                {job.experience_min}-{job.experience_max} years experience
              </span>
              <span className="flex items-center gap-2">
                <Briefcase size={18} />
                {job.employment_type}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Job Details & Apply Section */}
      <section className="py-12">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Role Summary */}
              {job.role_summary && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl p-6 border border-gray-100"
                >
                  <h2 className="text-xl font-bold text-[#320547] mb-4 flex items-center gap-2">
                    <Building2 size={22} className="text-[#95228A]" />
                    About the Role
                  </h2>
                  <p className="text-gray-600 leading-relaxed whitespace-pre-line" data-testid="role-summary">
                    {job.role_summary}
                  </p>
                </motion.div>
              )}
              
              {/* Responsibilities */}
              {job.responsibilities?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-xl p-6 border border-gray-100"
                >
                  <h2 className="text-xl font-bold text-[#320547] mb-4">Responsibilities</h2>
                  <ul className="space-y-3" data-testid="responsibilities-list">
                    {job.responsibilities.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 size={20} className="text-[#95228A] mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}
              
              {/* Requirements */}
              {job.requirements?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-xl p-6 border border-gray-100"
                >
                  <h2 className="text-xl font-bold text-[#320547] mb-4">Requirements</h2>
                  <ul className="space-y-3" data-testid="requirements-list">
                    {job.requirements.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 size={20} className="text-[#320547] mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}
              
              {/* Nice to Have */}
              {job.nice_to_have?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-xl p-6 border border-gray-100"
                >
                  <h2 className="text-xl font-bold text-[#320547] mb-4">Nice to Have</h2>
                  <ul className="space-y-3">
                    {job.nice_to_have.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 size={20} className="text-gray-400 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}
              
              {/* Benefits */}
              {job.benefits?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-gradient-to-br from-[#320547]/5 to-[#95228A]/5 rounded-xl p-6 border border-[#320547]/10"
                >
                  <h2 className="text-xl font-bold text-[#320547] mb-4">What We Offer</h2>
                  <ul className="grid md:grid-cols-2 gap-3">
                    {job.benefits.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 size={20} className="text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </div>
            
            {/* Sidebar - Apply Card */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="sticky top-24"
              >
                {/* Quick Info Card */}
                <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm mb-6">
                  <h3 className="text-lg font-bold text-[#320547] mb-4">Quick Info</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-500">Experience Required</p>
                      <p className="font-semibold text-gray-800">
                        {job.experience_min}-{job.experience_max} years
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-500">Location</p>
                      <p className="font-semibold text-gray-800">{job.location}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-500">Employment Type</p>
                      <p className="font-semibold text-gray-800">{job.employment_type}</p>
                    </div>
                    
                    {job.salary_range && (
                      <div>
                        <p className="text-sm text-gray-500">Salary Range</p>
                        <p className="font-semibold text-gray-800">{job.salary_range}</p>
                      </div>
                    )}
                    
                    {job.technologies?.length > 0 && (
                      <div>
                        <p className="text-sm text-gray-500 mb-2">Technologies</p>
                        <div className="flex flex-wrap gap-2">
                          {job.technologies.map((tech, i) => (
                            <span
                              key={i}
                              className="px-2 py-1 bg-[#320547]/10 text-[#320547] text-xs rounded-full"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Apply Card */}
                <div className="bg-gradient-to-br from-[#320547] to-[#95228A] rounded-xl p-6 text-white">
                  {!showApplicationForm && !submitted ? (
                    <>
                      <h3 className="text-xl font-bold mb-3">Interested in this role?</h3>
                      <p className="text-white/80 mb-6">
                        Apply now and take the next step in your career with Navaris Digital.
                      </p>
                      <Button
                        onClick={() => setShowApplicationForm(true)}
                        className="w-full bg-white text-[#320547] hover:bg-white/90 font-semibold"
                        data-testid="apply-now-btn"
                      >
                        Apply Now
                      </Button>
                    </>
                  ) : submitted ? (
                    <div className="text-center py-4">
                      <CheckCircle2 size={48} className="mx-auto text-green-400 mb-4" />
                      <h3 className="text-xl font-bold mb-2">Application Submitted!</h3>
                      <p className="text-white/80">
                        Thank you for applying. We'll review your application and get back to you soon.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4" data-testid="application-form">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold">Apply for this role</h3>
                        <button
                          type="button"
                          onClick={() => setShowApplicationForm(false)}
                          className="text-white/70 hover:text-white"
                        >
                          <X size={20} />
                        </button>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Full Name *</label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="John Doe"
                          className="flex h-11 w-full rounded-lg border border-white/30 bg-white/20 px-4 py-2 text-base font-medium text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 focus:bg-white/25"
                          required
                          data-testid="applicant-name"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Email *</label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="john@example.com"
                          className="flex h-11 w-full rounded-lg border border-white/30 bg-white/20 px-4 py-2 text-base font-medium text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 focus:bg-white/25"
                          required
                          data-testid="applicant-email"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Phone *</label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="+91 98765 43210"
                          className="flex h-11 w-full rounded-lg border border-white/30 bg-white/20 px-4 py-2 text-base font-medium text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 focus:bg-white/25"
                          required
                          data-testid="applicant-phone"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium mb-1">Resume * (PDF/Word)</label>
                        <input
                          ref={fileInputRef}
                          type="file"
                          onChange={handleFileChange}
                          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          className="hidden"
                          data-testid="resume-input"
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full p-3 border border-dashed border-white/30 rounded-lg bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
                        >
                          {formData.resume ? (
                            <>
                              <FileText size={20} />
                              <span className="truncate max-w-[150px]">{formData.resume.name}</span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFormData(prev => ({ ...prev, resume: null }));
                                }}
                                className="ml-2 text-white/70 hover:text-white"
                              >
                                <X size={16} />
                              </button>
                            </>
                          ) : (
                            <>
                              <Upload size={20} />
                              <span>Upload Resume</span>
                            </>
                          )}
                        </button>
                        <p className="text-xs text-white/60 mt-1">Max 5MB • PDF or Word</p>
                      </div>
                      
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-white text-[#320547] hover:bg-white/90 font-semibold"
                        data-testid="submit-application-btn"
                      >
                        {submitting ? (
                          <>
                            <Loader2 size={18} className="animate-spin mr-2" />
                            Submitting...
                          </>
                        ) : (
                          'Submit Application'
                        )}
                      </Button>
                    </form>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JobDetail;
